import React, { useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card';
import { Tooltip } from 'antd';
import { PumpRunPeriod } from './hooks/usePlantSensorData';
import { format, parseISO, differenceInMinutes, startOfDay } from 'date-fns';
import { da } from 'date-fns/locale';

/**
 * Format duration in minutes to a human-readable string
 */
function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);

  if (hours > 0 && mins > 0) {
    return `${hours}t ${mins}min`;
  } else if (hours > 0) {
    return `${hours}t`;
  } else {
    return `${mins}min`;
  }
}

/**
 * Get the hour position as percentage of 24 hours
 */
function getHourPosition(date: Date): number {
  const hours = date.getHours();
  const minutes = date.getMinutes();
  return ((hours * 60 + minutes) / (24 * 60)) * 100;
}

interface GroupedPeriods {
  day: string;
  dayFormatted: string;
  periods: {
    startPos: number; // % position from left
    width: number; // % width
    startTime: string;
    endTime: string | null;
    duration: number | null;
  }[];
  totalMinutes: number;
}

interface PumpTimelineProps {
  pumpRunPeriods: PumpRunPeriod[];
}

export function PumpTimeline({ pumpRunPeriods }: PumpTimelineProps) {
  const groupedData = useMemo(() => {
    if (!pumpRunPeriods || pumpRunPeriods.length === 0) return [];

    // Group periods by day
    const byDay = new Map<string, PumpRunPeriod[]>();
    pumpRunPeriods.forEach((period) => {
      const existing = byDay.get(period.day) || [];
      existing.push(period);
      byDay.set(period.day, existing);
    });

    // Transform to grouped format
    const result: GroupedPeriods[] = [];
    byDay.forEach((periods, day) => {
      const date = parseISO(day);
      let totalMinutes = 0;

      const transformedPeriods = periods.map((p) => {
        const startDate = parseISO(p.start_time);
        const endDate = p.end_time ? parseISO(p.end_time) : new Date();
        const duration = p.duration_minutes || differenceInMinutes(endDate, startDate);

        totalMinutes += duration;

        const startPos = getHourPosition(startDate);
        const endPos = p.end_time ? getHourPosition(endDate) : getHourPosition(new Date());
        const width = Math.max(endPos - startPos, 0.5); // Minimum 0.5% width for visibility

        return {
          startPos,
          width: Math.min(width, 100 - startPos), // Don't overflow
          startTime: format(startDate, 'HH:mm'),
          endTime: p.end_time ? format(endDate, 'HH:mm') : 'kører',
          duration,
        };
      });

      result.push({
        day,
        dayFormatted: format(date, 'EEE d. MMM', { locale: da }),
        periods: transformedPeriods,
        totalMinutes,
      });
    });

    // Sort by day
    result.sort((a, b) => a.day.localeCompare(b.day));

    return result;
  }, [pumpRunPeriods]);

  if (groupedData.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Pumpe aktivitet</CardTitle>
          <CardDescription>Ingen pumpe kørselsperioder fundet</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  // Hour labels for the timeline header
  const hourLabels = [0, 3, 6, 9, 12, 15, 18, 21];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Pumpe aktivitet tidslinje</CardTitle>
        <CardDescription>Viser hvornår pumpen har kørt i løbet af hver dag</CardDescription>
      </CardHeader>
      <CardContent>
        {/* Hour labels header */}
        <div className="flex mb-2">
          <div className="w-28 shrink-0" /> {/* Spacer for day labels */}
          <div className="flex-1 relative h-5">
            {hourLabels.map((hour) => (
              <span
                key={hour}
                className="absolute text-xs text-muted-foreground"
                style={{ left: `${(hour / 24) * 100}%`, transform: 'translateX(-50%)' }}
              >
                {hour.toString().padStart(2, '0')}
              </span>
            ))}
          </div>
          <div className="w-20 shrink-0 text-right text-xs text-muted-foreground">Total</div>
        </div>

        {/* Timeline rows */}
        <div className="space-y-1">
          {groupedData.map((dayData) => (
            <div key={dayData.day} className="flex items-center h-8">
              {/* Day label */}
              <div className="w-28 shrink-0 text-sm text-muted-foreground truncate pr-2">
                {dayData.dayFormatted}
              </div>

              {/* Timeline bar */}
              <div className="flex-1 relative h-6 bg-muted rounded">
                {/* Hour grid lines */}
                {hourLabels.map((hour) => (
                  <div
                    key={hour}
                    className="absolute h-full border-l border-border/50"
                    style={{ left: `${(hour / 24) * 100}%` }}
                  />
                ))}

                {/* Run periods */}
                {dayData.periods.map((period, idx) => (
                  <Tooltip
                    key={idx}
                    title={
                      <div className="text-xs">
                        <div>
                          {period.startTime} - {period.endTime}
                        </div>
                        <div>Varighed: {formatDuration(period.duration || 0)}</div>
                      </div>
                    }
                  >
                    <div
                      className="absolute h-full bg-green-500 rounded cursor-pointer hover:bg-green-600 transition-colors"
                      style={{
                        left: `${period.startPos}%`,
                        width: `${period.width}%`,
                      }}
                    />
                  </Tooltip>
                ))}
              </div>

              {/* Total for day */}
              <div className="w-20 shrink-0 text-right text-sm font-medium">
                {formatDuration(dayData.totalMinutes)}
              </div>
            </div>
          ))}
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 mt-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-500 rounded" />
            <span>Pumpe kører</span>
          </div>
          <div className="ml-auto">
            Total:{' '}
            <span className="font-medium">
              {formatDuration(groupedData.reduce((sum, d) => sum + d.totalMinutes, 0))}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
